/**
 * PORTED (REWRITTEN) — RO-3-00d / #44471.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_crm/leads.tsx`.
 *
 * LeadsView bootstrap is `crm.views.leads`. Detail composes mounted
 * `crm.leads.get` / timeline / contacts + opportunities.list.
 */

import { PageHeader, EmptyState } from "@excellent/ui";
import { LeadDetail } from "@excellent/ui/blocks/crm/lead-detail";
import { ActivityTimeline } from "@excellent/ui/blocks/crm/activity-timeline";
import { CustomerFinancialsCard } from "@excellent/ui/blocks/finance/customer-financials-card";
import { useApi } from "@excellent/ui/api";
import { crm } from "@excellent/ui/api/contracts/crm";
import { LeadActivityClient, LeadsListClient, NewLeadClient } from "./client";

export function LeadsView({ basePath }: { basePath: string }) {
  const { data, error, isLoading } = useApi(crm.views.leads, {});

  if (isLoading && !data) {
    return <p className="type-body text-muted-foreground">Loading…</p>;
  }
  if (error || !data) {
    return (
      <EmptyState
        title="Could not load leads"
        description={error?.message ?? "Try refreshing."}
      />
    );
  }

  return (
    <LeadsListClient
      basePath={basePath}
      initialLeads={data.leads}
      statusLabels={Object.fromEntries(
        Object.entries(data.statusLabels).map(([k, v]) => [Number(k), v]),
      )}
      sourceLabels={Object.fromEntries(
        Object.entries(data.sourceLabels).map(([k, v]) => [Number(k), v]),
      )}
      ownerLabels={Object.fromEntries(
        Object.entries(data.ownerLabels).map(([k, v]) => [Number(k), v]),
      )}
      smartViews={data.smartViews}
    />
  );
}

export function NewLeadView({ basePath }: { basePath: string }) {
  const { data: statusesData } = useApi(crm.settings.statuses_list, {});
  const { data: sourcesData } = useApi(crm.settings.sources_list, {});
  const statuses = (statusesData?.statuses ?? []).map((s) => ({
    slug: s.slug,
    name: s.name,
  }));
  const sources = (sourcesData?.sources ?? []).map((s) => ({
    slug: s.slug,
    name: s.name,
  }));

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-4">
      <PageHeader
        title="New lead"
        description="Capture a lead — the person, their company, and where they came from."
      />
      <NewLeadClient basePath={basePath} statuses={statuses} sources={sources} />
    </div>
  );
}

export function LeadDetailView({
  basePath: _basePath,
  id,
}: {
  basePath: string;
  id: string;
}) {
  const leadId = Number(id);
  const { data: lead, error, isLoading } = useApi(crm.leads.get, {
    params: { id: leadId },
  });
  const { data: timelineData } = useApi(crm.leads.timeline, {
    params: { id: leadId },
    query: {},
  });
  const { data: contactsData } = useApi(crm.leads.contacts_list, {
    params: { id: leadId },
  });
  const { data: statusesData } = useApi(crm.settings.statuses_list, {});
  const { data: sourcesData } = useApi(crm.settings.sources_list, {});
  const { data: pipelinesData } = useApi(crm.pipelines.list, {});
  const { data: oppsData } = useApi(crm.opportunities.list, {
    query: { lead_id: leadId },
  });

  if (isLoading && !lead) {
    return <p className="type-body text-muted-foreground">Loading…</p>;
  }
  if (error || !lead) {
    return (
      <EmptyState
        title="Lead not found"
        description={error?.message ?? "This lead may have been archived."}
      />
    );
  }

  const statuses = statusesData?.statuses ?? [];
  const sources = sourcesData?.sources ?? [];
  const pipelines = pipelinesData?.pipelines ?? [];
  const stageById = new Map<number, string>();
  for (const p of pipelines) {
    for (const s of p.stages ?? []) stageById.set(s.id, s.name);
  }
  const statusLabel = statuses.find((s) => s.id === lead.status_id)?.name ?? null;
  const sourceLabel = sources.find((s) => s.id === lead.source_id)?.name ?? null;
  const contacts = contactsData?.contacts ?? [];
  const timeline = timelineData?.rows ?? [];
  const opps = oppsData?.opportunities ?? [];

  return (
    <LeadDetail
      left={{
        display_name: lead.display_name,
        organization_name: undefined,
        organization_website: undefined,
        contacts: contacts.map((c) => ({
          display_name: c.display_name,
          // Contract is boolean; never pass sqlite 0/1 through.
          is_primary: Boolean(c.is_primary),
          email: null as string | null,
        })),
        status_label: statusLabel,
        source_label: sourceLabel,
        owner_label: null,
        tags: [],
      }}
      middle={
        <>
          <LeadActivityClient leadId={leadId} />
          <CustomerFinancialsCard
            lifetimeMinor={0}
            transactionCount={0}
            currency={null}
            lastTransactionAt={null}
            transactions={[]}
          />
          <ActivityTimeline rows={timeline as Parameters<typeof ActivityTimeline>[0]["rows"]} />
        </>
      }
      right={{
        opportunities: opps.map((o) => ({
          id: o.id,
          name: o.name,
          value_minor: o.value_minor,
          currency: o.currency,
          stage_name: stageById.get(o.stage_id) ?? "—",
          closed_won: o.closed_won === 1,
          closed_lost: o.closed_at != null && o.closed_won === 0,
        })),
      }}
    />
  );
}
